import { WaveInfluencerPostLiveStats } from '@round/api';
import { showNotification } from 'helpers';
import { useCallback, useEffect, useState } from 'react';

type FetchWaveCampaigns<T> = (planId: number, requestInit?: RequestInit) => Promise<{ data: T[] }>;

// Fetches the plan's wave live-stats blocks (one per wave campaign on the release).
// Standalone hook rather than a shared context: the Wave bars are the only consumer.
// The fetcher is passed in so each platform table supplies its own endpoint.
export function useWaveCampaigns<T extends WaveInfluencerPostLiveStats>(
    planId: number | undefined,
    fetchWaveCampaigns: FetchWaveCampaigns<T>
) {
    const [campaigns, setCampaigns] = useState<T[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Reset when the plan changes so we refetch instead of surfacing the previous plan's campaigns.
    useEffect(() => {
        setCampaigns([]);
        setIsInitialized(false);
        setHasError(false);
    }, [planId]);

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            if (!planId) {
                return;
            }

            setIsLoading(true);
            setHasError(false);
            try {
                const response = await fetchWaveCampaigns(planId, requestInit);
                setCampaigns(response.data);
                setIsInitialized(true);
                setIsLoading(false);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                showNotification('Could not load wave campaign stats', 'error');
                setHasError(true);
                setIsLoading(false);
            }
        },
        [planId, fetchWaveCampaigns]
    );

    return {
        campaigns,
        isInitialized,
        isLoading,
        hasError,
        init,
    };
}
